import type { AoSGameState, BuildTrackArgs, HexCoordinate, Direction } from '../../types';
import { coordToKey } from '../../types';
import type { Ctx } from 'boardgame.io';
import { getTileDefinition } from '../constants/tiles';
import { getUnfinishedSectionsAt } from '../utils/unfinishedSectionManager';
import { checkWouldFormSelfReferencingLink } from '../utils/linkCalculator';

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
  /** Parameters for i18n interpolation */
  params?: Record<string, string | number>;
}

/**
 * 線路建設のバリデーション
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param args - 線路建設引数
 * @returns バリデーション結果
 */
export function validateBuildTrack(
  G: AoSGameState,
  ctx: Ctx,
  args: BuildTrackArgs
): ValidationResult {
  const player = G.players[ctx.currentPlayer];

  // 倒産プレイヤーチェック
  if (player.isBankrupt) {
    return {
      success: false,
      error: 'PLAYER_BANKRUPT',
      message: 'Bankrupt players cannot build track'
    };
  }

  // タイル定義を取得
  const tileDefinition = getTileDefinition(args.tileType);
  if (!tileDefinition) {
    return {
      success: false,
      error: 'INVALID_TILE_TYPE',
      message: `Invalid tile type: ${args.tileType}`,
      params: { type: args.tileType }
    };
  }

  // 新都市タイルの場合は別カウント
  const isNewCityTile = tileDefinition.isNewCity === true;

  // 配置枚数チェック（新都市タイルは別カウント）
  if (!isNewCityTile) {
    const maxTiles = player.selectedAction === 'ENGINEER' ? 4 : 3;
    if (player.tilesPlacedThisTurn >= maxTiles) {
      return {
        success: false,
        error: 'INVALID_PLACEMENT_COUNT',
        message: `Maximum ${maxTiles} tiles can be placed this turn`,
        params: { max: maxTiles }
      };
    }
  }

  // 新都市タイル枚数制限（1ターン1枚）
  if (isNewCityTile) {
    const newCityPlaced = player.newCityTilesPlacedThisTurn ?? 0;
    if (newCityPlaced >= 1) {
      return {
        success: false,
        error: 'NEW_CITY_LIMIT_REACHED',
        message: 'Only 1 new city tile can be placed per turn'
      };
    }
  }

  // 新都市タイルの使用可否チェック（mapConfigに基づく）
  // デフォルト: A-H すべて配置可能（Common ルール）
  // Barbados のみ A-D 制限（特殊ルール）
  if (isNewCityTile) {
    const DEFAULT_NEW_CITY_TILES = [
      'NEW_CITY_A', 'NEW_CITY_B', 'NEW_CITY_C', 'NEW_CITY_D',
      'NEW_CITY_E', 'NEW_CITY_F', 'NEW_CITY_G', 'NEW_CITY_H'
    ];
    const allowedTiles = G.mapConfig?.availableNewCityTiles?.length
      ? G.mapConfig.availableNewCityTiles
      : DEFAULT_NEW_CITY_TILES;

    if (!allowedTiles.includes(args.tileType)) {
      return {
        success: false,
        error: 'NEW_CITY_NOT_AVAILABLE',
        message: `${args.tileType} is not available in ${G.mapName} map`,
        params: { type: args.tileType }
      };
    }
  }

  // Hex存在チェック
  const hexKey = coordToKey(args.hexCoordinate);
  const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);

  if (!hex) {
    return {
      success: false,
      error: 'OUT_OF_BOUNDS',
      message: `Hex ${hexKey} does not exist on the map`,
      params: { hex: hexKey }
    };
  }

  // 都市ヘクスチェック: Cityヘクスには線路を配置不可（都市は終端点）
  if (hex.city !== null) {
    return {
      success: false,
      error: 'CANNOT_BUILD_ON_CITY',
      message: 'Cannot place track on city hexes'
    };
  }

  // 山岳ヘクスチェック: mapConfig で禁止されている場合のみ
  // canBuildOnMountains が false の場合に禁止、undefined または true なら許可
  if (hex.terrain === 'mountains' && G.mapConfig?.canBuildOnMountains === false) {
    return {
      success: false,
      error: 'CANNOT_BUILD_ON_MOUNTAINS',
      message: 'Cannot place track on mountain hexes in this map'
    };
  }

  // 新都市化済みヘクスチェック: 新都市タイル配置済みの町はCityとして扱う
  if (hex.town?.isUrbanized === true) {
    return {
      success: false,
      error: 'CANNOT_BUILD_ON_URBANIZED_TOWN',
      message: 'Cannot place track on urbanized town (treated as city)'
    };
  }

  // 町タイルチェック: hex.townが存在するヘクスにのみ配置可能
  // 注: tileDefinition は上部で既に取得済み
  if (tileDefinition.isTown && !tileDefinition.isNewCity && !hex.town) {
    return {
      success: false,
      error: 'TOWN_TILE_REQUIRES_TOWN_HEX',
      message: `Town tiles can only be placed on town hexes`
    };
  }

  // 新都市タイルチェック: Urbanizationアクション選択時のみ、町ヘクスに配置可能
  if (tileDefinition.isNewCity) {
    // 新都市タイルは hex.town が存在するヘクスにのみ配置可能
    if (!hex.town) {
      return {
        success: false,
        error: 'NEW_CITY_REQUIRES_TOWN_HEX',
        message: 'New city tiles can only be placed on town hexes'
      };
    }

    // Urbanization アクション選択済みチェック
    if (player.selectedAction !== 'URBANIZATION') {
      return {
        success: false,
        error: 'URBANIZATION_REQUIRED',
        message: 'Urbanization action required to place new city tiles'
      };
    }

    // Urbanizationアクションマーカーが未使用であることを確認（Barbadosの場合）
    if ('actionMarkers' in G && G.actionMarkers) {
      const markers = G.actionMarkers as any;
      if (markers.urbanizationUsed) {
        return {
          success: false,
          error: 'URBANIZATION_ALREADY_USED',
          message: 'Urbanization action has already been used this game'
        };
      }
    }
  }

  // Town Disk チェック: Town ヘクスに Town タイル以外を配置する場合
  // - 各 Town には最大 1 枚まで
  // - マップ全体で 8 枚まで
  // - ただし「置き換え」で既に非Townタイルがある場合は、Town Disk は既に使用済みなのでスキップ
  if (hex.town && !tileDefinition.isTown && !tileDefinition.isNewCity) {
    const townId = hex.town.id;

    // 置き換えの場合、Town Disk は既に消費済みなのでチェックをスキップ
    const existingTileDef = hex.tile ? getTileDefinition(hex.tile.type) : null;
    const isReplacing = args.placementType === 'replace' && existingTileDef !== null;

    if (!isReplacing) {
      // この Town にすでに Town Disk が配置されているか
      if (G.townDiskPlaced && G.townDiskPlaced[townId]) {
        return {
          success: false,
          error: 'TOWN_DISK_ALREADY_PLACED',
          message: `Town ${townId} already has a non-town tile placed (max: 1 per town)`,
          params: { town: townId }
        };
      }

      // マップ全体で Town Disk が残っているか
      if (G.townDisksRemaining !== undefined && G.townDisksRemaining <= 0) {
        return {
          success: false,
          error: 'NO_TOWN_DISKS_REMAINING',
          message: 'No town disks remaining (max: 8 total)'
        };
      }
    }
  }

  // 配置種別に応じたチェック
  if (args.placementType === 'place') {
    // 新都市タイルの場合は、既存タイルがあっても配置可能（上書き）
    if (!isNewCityTile) {
      // 通常タイル: Hexが空きであること
      if (hex.tile !== null) {
        return {
          success: false,
          error: 'HEX_OCCUPIED',
          message: `Hex ${hexKey} is already occupied`,
          params: { hex: hexKey }
        };
      }
    }
    // 新都市タイルは hex.tile があっても OK（後でサプライに戻す）

    // 新都市タイル以外の場合のみ接続チェック
    // （新都市タイルは町ヘクスに無条件配置可能）
    if (!isNewCityTile) {
      // Issue #26: 接続条件チェック（タイルの接続方向を考慮）
      const connectionCheck = validateConnection(G, ctx, args.hexCoordinate, args.tileType, args.rotation);
      if (!connectionCheck.success) {
        return connectionCheck;
      }

      // 海への接続チェック
      const seaConnectionCheck = validateNoSeaConnection(G, args.hexCoordinate, args.tileType, args.rotation);
      if (!seaConnectionCheck.success) {
        return seaConnectionCheck;
      }
    }
  } else if (args.placementType === 'replace') {
    // 交換: 既存タイルが存在すること
    if (hex.tile === null) {
      return {
        success: false,
        error: 'NO_TRACK_TO_REPLACE',
        message: `No track to replace at ${hexKey}`,
        params: { hex: hexKey }
      };
    }

    // Issue #26: 置き換え時もマップ外への接続チェック（先にチェック）
    const seaConnectionCheck = validateNoSeaConnection(G, args.hexCoordinate, args.tileType, args.rotation);
    if (!seaConnectionCheck.success) {
      return seaConnectionCheck;
    }

    // 置き換えバリデーション（単線→複線、単線→単線、複線→複線をサポート）
    const replaceValidation = validateTrackReplacement(G, ctx, args.hexCoordinate, hex.tile, args.tileType, args.rotation);
    if (!replaceValidation.success) {
      return replaceValidation;
    }
  }

  // 資金チェック: コスト支払いは passBuildTrack で一括払い（エンジニアボーナス対応）
  // 個別タイルのコスト不足チェックは削除
  // UI側で累積コストを表示し、所持金オーバー時は Finish ボタンを無効化する
  const tileDef = getTileDefinition(args.tileType);
  const isNewCity = tileDef?.isNewCity === true;

  // Issue #29: Self-referencing link チェック
  // タイルを配置することで同一の都市/町を両端とするリンクが形成される場合は拒否
  // Issue #30: Town タイルはスキップ（中心への接続であり、両端の概念がない）
  if (!isNewCity && tileDef && !tileDef.isTown) {
    const rotatedConnections = getRotatedConnections(tileDef.connections, args.rotation);
    const selfRefCheck = checkWouldFormSelfReferencingLink(
      G,
      args.hexCoordinate,
      rotatedConnections,
      ctx.currentPlayer
    );
    if (selfRefCheck.wouldForm) {
      return {
        success: false,
        error: 'WOULD_FORM_SELF_REFERENCING_LINK',
        message: `This placement would form a link from ${selfRefCheck.cityId} to itself`,
        params: { city: selfRefCheck.cityId ?? '' }
      };
    }
  }

  return { success: true };
}

/**
 * 接続条件のバリデーション
 * Issue #26: tileType と rotation を受け取り、タイルの接続方向をチェック
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param coord - 配置予定の座標
 * @param tileType - タイル種別
 * @param rotation - 回転角度
 * @returns バリデーション結果
 */
function validateConnection(
  G: AoSGameState,
  ctx: Ctx,
  coord: HexCoordinate,
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {

  // プレイヤーの既存タイルをチェック
  const playerHasTrack = G.map.some(hex =>
    hex.tile !== null && hex.tile.owner === ctx.currentPlayer
  );

  if (!playerHasTrack) {
    // 初回配置: タイルの接続方向が都市を向いていること
    const isAdjacentToCity = checkAdjacentToCity(G, coord, tileType, rotation);
    if (!isAdjacentToCity) {
      return {
        success: false,
        error: 'INVALID_CONNECTION',
        message: 'First track must connect to a city'
      };
    }
  } else {
    // 継続配置: タイルの接続方向が自分の既存線路または都市に接続していること
    const isConnected = checkConnectedToPlayerTrackOrCity(G, ctx, coord, tileType, rotation);
    if (!isConnected) {
      return {
        success: false,
        error: 'INVALID_CONNECTION',
        message: 'Track must be connected to your existing track or a city'
      };
    }
  }

  // Issue #26: 複線タイルの各線路の接続チェック
  const allPathsResult = validateAllPathsConnected(G, coord, tileType, rotation);
  if (!allPathsResult.success) {
    return allPathsResult;
  }

  return { success: true };
}

/**
 * 都市に接続しているかチェック（タイルの接続方向を考慮）
 * Issue #26: タイルの接続方向が都市を向いているかを確認
 *
 * @param G - ゲーム状態
 * @param coord - 座標
 * @param tileType - タイル種別
 * @param rotation - 回転角度
 * @returns タイルの接続方向が都市を向いていればtrue
 */
function checkAdjacentToCity(
  G: AoSGameState,
  coord: HexCoordinate,
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): boolean {
  const tileDef = getTileDefinition(tileType);
  if (!tileDef) return false;

  // タイルの各接続方向について、都市への接続を確認
  for (const connectionGroup of tileDef.connections) {
    for (const baseEdge of connectionGroup) {
      const actualEdge = (baseEdge + rotation) % 6;
      const adjacentCoord = getAdjacentCoord(coord, actualEdge as 0 | 1 | 2 | 3 | 4 | 5);
      const adjacentKey = coordToKey(adjacentCoord);
      const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === adjacentKey);

      if (adjacentHex?.city !== null) {
        return true; // タイルの接続方向が都市を向いている
      }
    }
  }
  return false;
}

/**
 * プレイヤーの既存線路または都市に接続しているかチェック（タイルの接続方向を考慮）
 * Issue #26: タイルの接続方向と隣接タイルの逆方向接続をチェック
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param coord - 座標
 * @param tileType - タイル種別
 * @param rotation - 回転角度
 * @returns タイルの接続方向が都市または既存線路に接続していればtrue
 */
function checkConnectedToPlayerTrackOrCity(
  G: AoSGameState,
  ctx: Ctx,
  coord: HexCoordinate,
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): boolean {
  const tileDef = getTileDefinition(tileType);
  if (!tileDef) return false;

  // タイルの各接続方向について接続をチェック
  for (const connectionGroup of tileDef.connections) {
    for (const baseEdge of connectionGroup) {
      const actualEdge = (baseEdge + rotation) % 6;

      // isEdgeConnected は都市、都市化済み町、または逆方向接続を持つ隣接タイルへの接続をチェック
      if (isEdgeConnected(G, coord, actualEdge)) {
        // さらに、隣接タイルの場合は自分の所有かどうかをチェック
        const adjacentCoord = getAdjacentCoord(coord, actualEdge as 0 | 1 | 2 | 3 | 4 | 5);
        const adjacentKey = coordToKey(adjacentCoord);
        const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === adjacentKey);

        if (!adjacentHex) continue;

        // 都市に接続
        if (adjacentHex.city !== null) {
          return true;
        }

        // 都市化された町に接続
        if (adjacentHex.town?.isUrbanized && adjacentHex.town?.newCity) {
          return true;
        }

        // 自分の線路に接続（逆方向接続を持っている場合のみ）
        if (adjacentHex.tile !== null && adjacentHex.tile.owner === ctx.currentPlayer) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * 隣接座標を取得（簡易版）
 * @param coord - 基準座標
 * @param direction - 方向（0-5）
 * @returns 隣接座標
 */
function getAdjacentCoord(
  coord: HexCoordinate,
  direction: 0 | 1 | 2 | 3 | 4 | 5
): HexCoordinate {
  const AXIAL_DIRECTIONS: Record<number, HexCoordinate> = {
    0: { q: +1, r: -1 },  // NE
    1: { q: +1, r:  0 },  // E
    2: { q:  0, r: +1 },  // SE
    3: { q: -1, r: +1 },  // SW
    4: { q: -1, r:  0 },  // W
    5: { q:  0, r: -1 },  // NW
  };

  const offset = AXIAL_DIRECTIONS[direction];
  return {
    q: coord.q + offset.q,
    r: coord.r + offset.r
  };
}

/**
 * 複線タイルの各線路について、両端のいずれかが接続しているか確認
 * Issue #26: 複線タイルのすべての線路が接続されている必要がある
 *
 * @param G - ゲーム状態
 * @param coord - 配置座標
 * @param tileType - タイル種別
 * @param rotation - 回転角度
 * @returns バリデーション結果
 */
function validateAllPathsConnected(
  G: AoSGameState,
  coord: HexCoordinate,
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  const tileDef = getTileDefinition(tileType);
  if (!tileDef) return { success: true };

  // Town タイルはスキップ
  // Town タイルの connections は [[0], [2], [4]] のような単一辺形式（中心への接続）
  // 複線タイルの「両端接続チェック」は適用不可
  if (tileDef.isTown) {
    return { success: true };
  }

  // 単線タイルはスキップ（既存チェックで対応）
  if (tileDef.connections.length <= 1) {
    return { success: true };
  }

  // Issue #31: 配置先のヘクスに Town がある場合、全線路パスは Town に接続しているとみなす
  // Town ヘクスに複線タイルを配置する場合、各線路パスはその Town を通過/終端するため
  // 浮遊チェックは不要
  const hexKey = coordToKey(coord);
  const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);
  if (hex && hex.town !== null) {
    return { success: true };
  }

  // 各線路（connection pair）について接続を確認
  for (let i = 0; i < tileDef.connections.length; i++) {
    const connectionPair = tileDef.connections[i];
    const [baseEdge1, baseEdge2] = connectionPair;
    const actualEdge1 = (baseEdge1 + rotation) % 6;
    const actualEdge2 = (baseEdge2 + rotation) % 6;

    const edge1Connected = isEdgeConnected(G, coord, actualEdge1);
    const edge2Connected = isEdgeConnected(G, coord, actualEdge2);

    // 両端とも接続されていない場合はエラー
    if (!edge1Connected && !edge2Connected) {
      return {
        success: false,
        error: 'FLOATING_TRACK_PATH',
        message: `Track path ${i + 1} is not connected to any city or existing track`,
        params: { num: i + 1 }
      };
    }
  }

  return { success: true };
}

/**
 * マップ外、壁への接続チェック
 * Issue #26: タイルの接続先がマップ外を向いている場合はエラー
 * 壁（blockedEdges）がある方向への接続もエラー
 *
 * 注: River（川）は通常ヘクスとして敷設可能。Sea（海）はマップ外として処理。
 *
 * @param G - ゲーム状態
 * @param coord - 配置予定の座標
 * @param tileType - タイル種別
 * @param rotation - 回転角度
 * @returns バリデーション結果
 */
function validateNoSeaConnection(
  G: AoSGameState,
  coord: HexCoordinate,
  tileType: string,
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  const tileDefinition = getTileDefinition(tileType);
  if (!tileDefinition) {
    return { success: true }; // タイル定義が見つからない場合はスキップ
  }

  // 配置先Hexを取得
  const hexKey = coordToKey(coord);
  const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);

  // タイルの接続方向を取得（rotation適用後）
  const connections = tileDefinition.connections;

  // 各接続から伸びる方向をチェック
  for (const connectionGroup of connections) {
    for (const baseDir of connectionGroup) {
      // 回転を適用
      const actualDir = ((baseDir + rotation) % 6) as Direction;

      // 壁チェック: 配置先Hexから見て壁がある方向
      if (hex?.blockedEdges?.includes(actualDir)) {
        return {
          success: false,
          error: 'WALL_BLOCKS_CONNECTION',
          message: `Tile cannot connect through wall (direction: ${actualDir})`,
          params: { dir: actualDir }
        };
      }

      // 隣接Hexを取得
      const adjacentCoord = getAdjacentCoord(coord, actualDir);
      const adjacentKey = coordToKey(adjacentCoord);
      const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === adjacentKey);

      // Issue #26: Hexが存在しない場合はマップ外 → エラー
      if (!adjacentHex) {
        return {
          success: false,
          error: 'CANNOT_CONNECT_TO_MAP_EDGE',
          message: `Tile cannot connect outside the map (direction: ${actualDir})`,
          params: { dir: actualDir }
        };
      }

      // 壁チェック: 隣接Hexから見て壁がある方向（逆方向）
      const oppositeDir = getOppositeDirection(actualDir) as Direction;
      if (adjacentHex.blockedEdges?.includes(oppositeDir)) {
        return {
          success: false,
          error: 'WALL_BLOCKS_CONNECTION',
          message: `Tile cannot connect through wall (direction: ${actualDir})`,
          params: { dir: actualDir }
        };
      }

      // 注: River（川）への接続は許可される（川は通常ヘクスとして敷設可能）
      // Sea（海）はマップに含まれないヘクスとして処理され、上記のマップ外チェックで拒否される
    }
  }

  return { success: true };
}

/**
 * 線路置き換えのバリデーション
 *
 * ## 置き換えパターン
 *
 * ### パターン1: 単線→複線
 * - 元の単線の所有者は問わない
 * - 元の単線の形状を維持すること
 * - 新たに追加される経路の両端のいずれかが接続していること
 *
 * ### パターン2: 単線→単線（方向転換）
 * - 自分所有の線路であること
 * - 未完成リンクの先端にあるタイル（開放端を持つ）であること
 * - タイルへの元の入口（entryEdge）は変更しないこと
 *
 * ### パターン3: 複線→複線
 * - 出口が変更される経路についてはパターン2と同様の制約が適用される
 * - 他プレイヤーの経路は維持すること
 *
 * @see docs/specifications/logic/common/phase_04_build_track.md Section 3.6
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.ioコンテキスト
 * @param coord - 配置座標
 * @param existingTile - 既存のタイル
 * @param newTileType - 新しいタイル種別
 * @param newRotation - 新しいタイルの回転
 * @returns バリデーション結果
 */
function validateTrackReplacement(
  G: AoSGameState,
  ctx: Ctx,
  coord: HexCoordinate,
  existingTile: import('../../types').TrackTile,
  newTileType: string,
  newRotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  const existingTileDef = getTileDefinition(existingTile.type);
  const newTileDef = getTileDefinition(newTileType);

  if (!existingTileDef || !newTileDef) {
    return {
      success: false,
      error: 'INVALID_TILE_TYPE',
      message: 'Invalid tile type for replacement'
    };
  }

  // Town ヘクス上かどうかを判定
  const hexKey = coordToKey(coord);
  const hex = G.map.find(h => coordToKey(h.coordinate) === hexKey);
  const isOnTownHex = hex?.town !== null && hex?.town !== undefined;

  // Issue #11: Town ヘクス上での置き換え
  // ルール: 既存の辺が保持されていればOK（辺が増えるのは問題ない）
  if (isOnTownHex) {
    return validateOnTownHex(existingTile, existingTileDef, newTileDef, newRotation);
  }

  // 以下は通常ヘクス上での置き換え

  // Issue #11: Town タイルからの置き換え（Town/単線/複線 いずれも対応）
  if (existingTileDef.isTown) {
    return validateTownToAnyTile(G, coord, existingTile, existingTileDef, newTileDef, newRotation);
  }

  // Issue #11: 単線/複線 → Town タイルへの置き換え
  if (newTileDef.isTown) {
    return validateAnyToTown(existingTile, existingTileDef, newTileDef, newRotation);
  }

  // 既存タイルの接続数（1=単線、2=複線）
  const existingConnectionCount = existingTileDef.connections.length;
  // 新しいタイルの接続数
  const newConnectionCount = newTileDef.connections.length;

  // 既存タイルの接続を回転適用して取得
  const existingConnections = existingTileDef.connections.map(pair =>
    pair.map(edge => (edge + existingTile.rotation) % 6).sort((a, b) => a - b)
  );

  // 新しいタイルの接続を回転適用して取得
  const newConnections = newTileDef.connections.map(pair =>
    pair.map(edge => (edge + newRotation) % 6).sort((a, b) => a - b)
  );

  // パターン判定
  if (existingConnectionCount === 1 && newConnectionCount >= 2) {
    // パターン1: 単線→複線
    return validateSingleToDouble(G, coord, existingConnections, newConnections, newTileDef, newRotation);
  } else if (existingConnectionCount === 1 && newConnectionCount === 1) {
    // パターン2: 単線→単線（方向転換）
    return validateSingleToSingle(G, ctx, coord, existingTile, existingConnections, newConnections);
  } else if (existingConnectionCount >= 2 && newConnectionCount >= 2) {
    // パターン3: 複線→複線
    return validateDoubleToDouble(G, ctx, coord, existingTile, existingConnections, newConnections);
  } else {
    // 複線→単線は不可
    return {
      success: false,
      error: 'INVALID_REPLACEMENT_PATTERN',
      message: 'Cannot replace double track with single track'
    };
  }
}

/**
 * Issue #11: Town タイルから任意タイルへの置き換えバリデーション
 *
 * ルール: Town タイルの全エッジが新タイルに含まれていれば置き換え可能
 * - Town → Town: 元の辺が新Townの辺に含まれる
 * - Town → 単線: 元の辺が単線の両端に含まれる
 * - Town → 複線: 元の辺が複線のいずれかのパスに含まれる
 */
function validateTownToAnyTile(
  _G: AoSGameState,
  _coord: HexCoordinate,
  existingTile: import('../../types').TrackTile,
  existingTileDef: ReturnType<typeof getTileDefinition>,
  newTileDef: ReturnType<typeof getTileDefinition>,
  newRotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  // 1. Town タイルの全エッジを取得（回転適用）
  // Town タイル: connections: [[0], [2], [4]] - 各要素は長さ1
  const townEdges = existingTileDef!.connections.map(
    conn => (conn[0] + existingTile.rotation) % 6
  );

  // 2. 新タイルの全エッジを収集（回転適用）
  //    - Town タイル: [[0], [2], [4]] → {0, 2, 4}
  //    - 単線タイル: [[0, 3]] → {0, 3}
  //    - 複線タイル: [[0, 2], [3, 5]] → {0, 2, 3, 5}
  const newTileEdges = new Set<number>();
  for (const path of newTileDef!.connections) {
    for (const edge of path) {
      newTileEdges.add((edge + newRotation) % 6);
    }
  }

  // 3. Town の全エッジが新タイルに含まれているか確認
  for (const townEdge of townEdges) {
    if (!newTileEdges.has(townEdge)) {
      return {
        success: false,
        error: 'TOWN_EDGE_NOT_PRESERVED',
        message: `Town edge ${townEdge} not preserved in new tile`,
        params: { edge: townEdge }
      };
    }
  }

  return { success: true };
}

/**
 * Issue #11: 単線/複線 → Town タイルへの置き換えバリデーション
 *
 * ルール: 既存タイルの全エッジが新 Town タイルに含まれていれば置き換え可能
 * - 単線 → Town: 単線の両端が Town のエッジに含まれる
 * - 複線 → Town: 複線の全パスのエッジが Town のエッジに含まれる
 */
function validateAnyToTown(
  existingTile: import('../../types').TrackTile,
  existingTileDef: ReturnType<typeof getTileDefinition>,
  newTileDef: ReturnType<typeof getTileDefinition>,
  newRotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  // 1. 既存タイルの全エッジを取得（回転適用）
  const existingEdges = new Set<number>();
  for (const path of existingTileDef!.connections) {
    for (const edge of path) {
      existingEdges.add((edge + existingTile.rotation) % 6);
    }
  }

  // 2. 新 Town タイルの全エッジを取得（回転適用）
  // Town タイル: [[0], [2], [4]] - 各要素は長さ1
  const townEdges = new Set<number>();
  for (const conn of newTileDef!.connections) {
    townEdges.add((conn[0] + newRotation) % 6);
  }

  // 3. 既存タイルの全エッジが新 Town タイルに含まれているか確認
  for (const existingEdge of existingEdges) {
    if (!townEdges.has(existingEdge)) {
      return {
        success: false,
        error: 'EXISTING_EDGE_NOT_IN_TOWN',
        message: `Existing edge ${existingEdge} not present in new Town tile`,
        params: { edge: existingEdge }
      };
    }
  }

  return { success: true };
}

/**
 * Issue #11: Town ヘクス上での置き換えバリデーション
 *
 * ルール: 既存タイルの全エッジが新タイルに含まれていれば置き換え可能
 * （辺が増えるのは問題ない、減ってはいけない）
 *
 * 対応パターン:
 * - Town → 単線/複線/Town
 * - 単線 → 複線/Town
 * - 複線 → 複線/Town
 */
function validateOnTownHex(
  existingTile: import('../../types').TrackTile,
  existingTileDef: ReturnType<typeof getTileDefinition>,
  newTileDef: ReturnType<typeof getTileDefinition>,
  newRotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  // 1. 既存タイルの全エッジを取得（回転適用）
  const existingEdges = new Set<number>();
  for (const path of existingTileDef!.connections) {
    for (const edge of path) {
      existingEdges.add((edge + existingTile.rotation) % 6);
    }
  }

  // 2. 新タイルの全エッジを取得（回転適用）
  const newEdges = new Set<number>();
  for (const path of newTileDef!.connections) {
    for (const edge of path) {
      newEdges.add((edge + newRotation) % 6);
    }
  }

  // 3. 既存タイルの全エッジが新タイルに含まれているか確認
  for (const existingEdge of existingEdges) {
    if (!newEdges.has(existingEdge)) {
      return {
        success: false,
        error: 'EXISTING_EDGE_NOT_PRESERVED',
        message: `Existing edge ${existingEdge} not preserved in new tile`,
        params: { edge: existingEdge }
      };
    }
  }

  return { success: true };
}

/**
 * パターン1: 単線→複線の置き換えバリデーション
 * - 元の単線の所有者は問わない
 * - 元の単線の形状を維持すること
 * - 新たに追加される経路の両端のいずれかが接続していること
 */
function validateSingleToDouble(
  G: AoSGameState,
  coord: HexCoordinate,
  existingConnections: number[][],
  newConnections: number[][],
  newTileDef: ReturnType<typeof getTileDefinition>,
  newRotation: 0 | 1 | 2 | 3 | 4 | 5
): ValidationResult {
  const existingPath = existingConnections[0]; // 単線なので1つだけ

  // 既存の経路が新しいタイルに含まれているかチェック
  let existingPathIndex = -1;
  const containsExistingPath = newConnections.some((newPath, index) => {
    if (newPath.length !== existingPath.length) return false;
    const matches = newPath.every((edge, i) => edge === existingPath[i]);
    if (matches) existingPathIndex = index;
    return matches;
  });

  if (!containsExistingPath) {
    return {
      success: false,
      error: 'INVALID_REPLACEMENT_CONNECTION',
      message: 'New tile must contain the existing track path'
    };
  }

  // Issue #17: 新経路の接続チェック
  // 新たに追加される経路（既存経路ではない方）を特定
  const newPathIndex = existingPathIndex === 0 ? 1 : 0;
  const newPath = newTileDef!.connections[newPathIndex].map(
    edge => (edge + newRotation) % 6
  ) as [number, number];

  // 新経路の各端点が隣接タイルまたは都市に接続しているかチェック
  const newPathConnected = checkNewPathConnection(G, coord, newPath);
  if (!newPathConnected) {
    return {
      success: false,
      error: 'NEW_PATH_NOT_CONNECTED',
      message: 'New track path must connect to an adjacent tile or city'
    };
  }

  return { success: true };
}

/**
 * パターン2: 単線→単線の置き換えバリデーション（方向転換）
 * - 自分所有の線路であること
 * - 未完成リンクの先端にあるタイル（開放端を持つ）であること
 * - タイルへの元の入口（entryEdge）は変更しないこと
 */
function validateSingleToSingle(
  G: AoSGameState,
  ctx: Ctx,
  coord: HexCoordinate,
  _existingTile: import('../../types').TrackTile,
  _existingConnections: number[][],
  newConnections: number[][]
): ValidationResult {
  const hexKey = coordToKey(coord);

  // 1. 未完成リンクの先端にあること（開放端を持つ）
  const sections = getUnfinishedSectionsAt(G, coord);
  if (sections.length === 0) {
    return {
      success: false,
      error: 'NOT_AT_UNFINISHED_SECTION_END',
      message: 'Single-to-single replacement is only allowed at the end of an unfinished section'
    };
  }

  // 2. 所有権チェック（自分または所有者なし）
  const hasOwnOrUnowned = sections.some(section =>
    section.owner === ctx.currentPlayer || section.owner === ''
  );

  if (!hasOwnOrUnowned) {
    return {
      success: false,
      error: 'CANNOT_REPLACE_OTHER_PLAYER_TRACK',
      message: 'Cannot replace another player\'s track'
    };
  }

  // 3. 入口（entryEdge）を維持しているかチェック
  const newPath = newConnections[0]; // 単線なので1つだけ

  // entryEdge（接続済み側）を特定
  // UnfinishedSection の entryEdge がそれに該当
  for (const section of sections) {
    const entryEdge = section.entryEdge;
    // 新しいタイルも同じ entryEdge を持っていること
    if (!newPath.includes(entryEdge)) {
      return {
        success: false,
        error: 'REPLACEMENT_BREAKS_CONNECTION',
        message: `New tile must maintain connection to direction ${entryEdge}`,
        params: { edge: entryEdge }
      };
    }
  }

  // 4. 完成リンク上のタイルではないことを確認
  const isOnCompletedLink = G.completedLinks.some(link =>
    link.path.some(pathCoord => coordToKey(pathCoord) === hexKey)
  );

  if (isOnCompletedLink) {
    return {
      success: false,
      error: 'CANNOT_REPLACE_COMPLETED_LINK',
      message: 'Cannot replace tiles that are part of a completed link'
    };
  }

  return { success: true };
}

/**
 * パターン3: 複線→複線の置き換えバリデーション
 * - 出口が変更される経路についてはパターン2と同様の制約が適用される
 * - 他プレイヤーの経路は維持すること
 *
 * Issue #28: connectionIndex と existingConnections のインデックス対応修正
 * UnfinishedSection.connectionIndex は tileDef.connections の元のインデックスを参照している。
 * そのため、経路マッチングには entryEdge/exitEdge を使用して正しい section を特定する。
 */
function validateDoubleToDouble(
  G: AoSGameState,
  ctx: Ctx,
  coord: HexCoordinate,
  existingTile: import('../../types').TrackTile,
  existingConnections: number[][],
  newConnections: number[][]
): ValidationResult {
  const hexKey = coordToKey(coord);

  // 完成リンク上のタイルではないことを確認
  const isOnCompletedLink = G.completedLinks.some(link =>
    link.path.some(pathCoord => coordToKey(pathCoord) === hexKey)
  );

  if (isOnCompletedLink) {
    return {
      success: false,
      error: 'CANNOT_REPLACE_COMPLETED_LINK',
      message: 'Cannot replace tiles that are part of a completed link'
    };
  }

  // 全ての UnfinishedSection を取得
  const allSections = getUnfinishedSectionsAt(G, coord);

  // 各経路について変更があるかチェック
  for (let i = 0; i < existingConnections.length; i++) {
    const existingPath = existingConnections[i];

    // 対応する新経路を探す
    const matchingNewPath = newConnections.find(newPath =>
      newPath.length === existingPath.length &&
      newPath.every((edge, j) => edge === existingPath[j])
    );

    if (!matchingNewPath) {
      // この経路は変更される → パターン2と同様の制約を適用

      // 所有権を確認
      let pathOwner: string | undefined;
      if (existingTile.owners && existingTile.owners.length > i) {
        pathOwner = existingTile.owners[i].playerId;
      } else {
        pathOwner = existingTile.owner;
      }

      // 他プレイヤーの経路を変更しようとしている場合はエラー
      if (pathOwner && pathOwner !== ctx.currentPlayer && pathOwner !== '') {
        return {
          success: false,
          error: 'CANNOT_MODIFY_OTHER_PLAYER_PATH',
          message: 'Cannot modify another player\'s track path in a double track replacement'
        };
      }

      // Issue #28 修正: 経路のエッジを使って UnfinishedSection を特定
      // existingPath の各エッジが entryEdge または exitEdge に含まれる section を探す
      const relevantSection = allSections.find(s => {
        // この section の entryEdge と exitEdge が existingPath に含まれているか
        return existingPath.includes(s.entryEdge) && existingPath.includes(s.exitEdge);
      });

      if (!relevantSection) {
        return {
          success: false,
          error: 'PATH_NOT_AT_UNFINISHED_SECTION_END',
          message: 'Modified path must be at the end of an unfinished section'
        };
      }

      // entryEdge を維持しているかチェック
      // 新しいタイルの対応する経路を探す
      const entryEdge = relevantSection.entryEdge;
      const newPathForThisRoute = newConnections.find(newPath =>
        newPath.includes(entryEdge) && !existingConnections.some(existPath =>
          existPath.length === newPath.length && existPath.every((e, j) => e === newPath[j])
        )
      );

      if (!newPathForThisRoute || !newPathForThisRoute.includes(entryEdge)) {
        return {
          success: false,
          error: 'REPLACEMENT_BREAKS_CONNECTION',
          message: `New tile must maintain connection to direction ${entryEdge}`,
          params: { edge: entryEdge }
        };
      }
    }
  }

  return { success: true };
}

/**
 * 新経路が隣接タイルまたは都市に接続しているかチェック
 *
 * Issue #17: 置き換え時、新経路の両端のいずれかが接続している必要がある
 *
 * @param G - ゲーム状態
 * @param coord - タイル配置座標
 * @param newPath - 新経路の接続方向 [dir1, dir2]
 * @returns 少なくとも一方の端点が接続していればtrue
 */
function checkNewPathConnection(
  G: AoSGameState,
  coord: HexCoordinate,
  newPath: [number, number]
): boolean {
  // 両端の方向それぞれをチェック
  for (const dir of newPath) {
    const adjacentCoord = getAdjacentCoord(coord, dir as 0 | 1 | 2 | 3 | 4 | 5);
    const adjacentKey = coordToKey(adjacentCoord);
    const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === adjacentKey);

    if (!adjacentHex) continue; // マップ外

    // 都市に接続
    if (adjacentHex.city !== null) {
      return true;
    }

    // 都市化されたTownに接続
    if (adjacentHex.town?.isUrbanized && adjacentHex.town?.newCity) {
      return true;
    }

    // 隣接タイルに接続
    if (adjacentHex.tile !== null) {
      // 隣接タイルの接続方向を取得
      const oppositeDir = getOppositeDirection(dir as 0 | 1 | 2 | 3 | 4 | 5);
      const adjacentTileDef = getTileDefinition(adjacentHex.tile.type);
      if (adjacentTileDef) {
        // 隣接タイルの接続（回転適用済み）を確認
        const adjacentConnections = adjacentTileDef.connections.map(pair =>
          pair.map(edge => (edge + adjacentHex.tile!.rotation) % 6)
        );

        // 隣接タイルがこちらへの接続を持っているか
        const hasConnection = adjacentConnections.some(pair =>
          pair.includes(oppositeDir)
        );

        if (hasConnection) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * 反対方向を取得
 */
function getOppositeDirection(dir: 0 | 1 | 2 | 3 | 4 | 5): number {
  return (dir + 3) % 6;
}

/**
 * タイルの特定の接続方向が都市または既存線路に接続しているか確認
 * Issue #26: checkNewPathConnection() のロジックを単一方向用に抽出
 *
 * @param G - ゲーム状態
 * @param coord - タイル配置座標
 * @param edge - チェックする方向（0-5）
 * @returns 接続していればtrue
 */
function isEdgeConnected(
  G: AoSGameState,
  coord: HexCoordinate,
  edge: number
): boolean {
  const adjacentCoord = getAdjacentCoord(coord, edge as 0 | 1 | 2 | 3 | 4 | 5);
  const adjacentKey = coordToKey(adjacentCoord);
  const adjacentHex = G.map.find(h => coordToKey(h.coordinate) === adjacentKey);

  // マップ外は接続なし
  if (!adjacentHex) return false;

  // 都市に接続
  if (adjacentHex.city !== null) return true;

  // 都市化された町に接続
  if (adjacentHex.town?.isUrbanized && adjacentHex.town?.newCity) return true;

  // 既存線路に接続（逆方向の接続を確認）
  if (adjacentHex.tile !== null) {
    const oppositeEdge = getOppositeDirection(edge as 0 | 1 | 2 | 3 | 4 | 5);
    const adjacentTileDef = getTileDefinition(adjacentHex.tile.type);
    if (adjacentTileDef) {
      // 隣接タイルの接続（回転適用済み）を確認
      const adjacentConnections = adjacentTileDef.connections.map(pair =>
        pair.map(e => (e + adjacentHex.tile!.rotation) % 6)
      );
      // 隣接タイルがこちらへの接続を持っているか
      return adjacentConnections.some(pair => pair.includes(oppositeEdge));
    }
  }

  return false;
}

/**
 * タイルが交差する複線かどうかを判定
 * 交差する複線: crossing カテゴリのタイル
 * 交差しない複線: coexist カテゴリのタイル
 *
 * @param tileType - タイル種別
 * @returns true=交差する複線, false=交差しない複線または単線
 */
export function isCrossingDoubleTrack(tileType: string): boolean {
  const tileDef = getTileDefinition(tileType);
  if (!tileDef) return false;
  return tileDef.category === 'crossing';
}

/**
 * タイルの接続情報に回転を適用
 *
 * @param connections - タイルの接続情報（基本方向）
 * @param rotation - 回転角度 (0-5)
 * @returns 回転適用後の接続情報
 */
function getRotatedConnections(
  connections: number[][],
  rotation: 0 | 1 | 2 | 3 | 4 | 5
): number[][] {
  return connections.map(pair =>
    pair.map(edge => (edge + rotation) % 6)
  );
}

